import './poof.css';

/**
 * A session unplanned from the calendar goes in a poof rather than just blinking off (Petr): a copy of it shrinks fast
 * and fades where it was, hidden by a soft grey cloud that billows up over it and drifts away. Petr picked it from
 * clickable samples; it replaced a puff of course-coloured blobs that looked odd and drifted over the day headers.
 *
 * It runs just before the session leaves the store, on the element still on screen. Both go into the calendar (its
 * closest `.fc`), so the calendar's own styles still dress the copy - the way FullCalendar's drag mirror works - inside
 * a layer cut off at the top and bottom of the part of the calendar that scrolls, so nothing draws over the headers
 * above it (poof.css). Nothing is shown for an element that isn't visible, or for a student who asked their system
 * for less motion: the session then simply goes, as it always did.
 */

// Until the cloud has faded (poof.css).
const POOF_MS = 700;
// How long the sagas hold the session in the store once it has poofed: the ghost's own 130 ms. Taking it out at once
// redrew the whole calendar in the poof's first frames, which made it stutter; the ghost covers the wait.
export const POOF_SETTLE_MS = 130;

const prefersLessMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Classes that run an animation of their own on a session or a gap, which would win over its ghost's.
const ANIMATED_CLASSES = [
  'planned-task-event-auto-plan-intro',
  'planned-task-event-ai-added',
  'appears-after-auto-plan',
  'appears-after-unplan'
];
// How long a gap that went with a session stays hidden if the calendar never redraws it: by then it would have.
const STRIP_RESTORE_MS = 2000;

type Box = { left: number; top: number; width: number; height: number };

// Where the poof may draw: the inside of the calendar's scrolling part, scroll bars left out. A session dragged out
// of the calendar is a mirror outside it, and poofs where it was dropped.
const clipBox = (el: HTMLElement): Box => {
  const scroller = el.closest<HTMLElement>('.fc-scroller');
  if (!scroller) return { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
  const rect = scroller.getBoundingClientRect();
  return {
    left: rect.left + scroller.clientLeft,
    top: rect.top + scroller.clientTop,
    width: scroller.clientWidth || rect.width,
    height: scroller.clientHeight || rect.height
  };
};

/**
 * The puffs a cloud over `rect` is made of, as centres measured from the session's middle, sizes and how far each
 * drifts sideways. Scattered out toward its corners, so none of the session shows round them as it shrinks; more of
 * them for a taller session, each about three quarters of its narrower side, so a short one is covered and a long one
 * doesn't billow across the week.
 */
export const cloudPuffs = (rect: { width: number; height: number }, random = Math.random) => {
  const base = Math.min(Math.max(Math.min(rect.width, rect.height) * 0.75, 22), 38);
  const count = Math.min(Math.max(Math.round(rect.height / 18) + 5, 8), 14);
  return Array.from({ length: count }, () => {
    const x = (random() - 0.5) * 0.72 * rect.width;
    const y = (random() - 0.5) * 0.64 * rect.height;
    // Outward a little, with the side it starts on.
    return { x, y, size: Math.round(base * (0.8 + random() * 0.45)), drift: Math.round(x * 0.3) };
  });
};

type Placed = { el: HTMLElement; rect: DOMRect };
type Measured = Placed & { clip: Box; strips: Placed[] };

/**
 * The extra time touching `rect` in the same day: the short gaps between it and the sessions next to it. They go with
 * it, since the time they part it from is freed with it; left standing, they framed an empty slot until the calendar
 * redrew the free time there, after the cloud had gone.
 */
const touchingStrips = (el: HTMLElement, rect: DOMRect): Placed[] => {
  const day = el.closest('.fc-timegrid-col');
  if (!day) return [];
  const strips: Placed[] = [];
  day.querySelectorAll<HTMLElement>('.fc-bg-event.extra-time-event').forEach(strip => {
    const box = strip.getBoundingClientRect();
    const touches = Math.abs(box.bottom - rect.top) <= 3 || Math.abs(box.top - rect.bottom) <= 3;
    if (box.height && touches && box.right > rect.left && box.left < rect.right) strips.push({ el: strip, rect: box });
  });
  return strips;
};

// Everything the poof needs to know about `el`, or nothing for an element that isn't visible, or under reduced motion.
// Read for every session before any poofs: reading between the poofs made the browser lay the calendar out again for
// each one.
const measure = (el: HTMLElement): Measured | undefined => {
  const rect = el.getBoundingClientRect();
  if (!rect.width || !rect.height || getComputedStyle(el).visibility === 'hidden' || prefersLessMotion()) {
    return undefined;
  }
  const clip = clipBox(el);
  // Scrolled out of sight: nothing to see go.
  if (
    rect.right <= clip.left ||
    rect.left >= clip.left + clip.width ||
    rect.bottom <= clip.top ||
    rect.top >= clip.top + clip.height
  ) {
    return undefined;
  }
  return { el, rect, clip, strips: touchingStrips(el, rect) };
};

const poof = ({ el, rect, clip, strips }: Measured) => {
  const container = el.closest<HTMLElement>('.fc') || document.body;

  const layer = document.createElement('div');
  layer.className = 'poof-clip';
  Object.assign(layer.style, {
    left: `${clip.left}px`,
    top: `${clip.top}px`,
    width: `${clip.width}px`,
    height: `${clip.height}px`
  });

  // Its gaps fade under the session's ghost. A gap between two sessions that both go is only taken once.
  strips.forEach(strip => {
    if (strip.el.style.visibility === 'hidden') return;
    const wrap = document.createElement('div');
    wrap.className = 'poof-strip';
    Object.assign(wrap.style, {
      left: `${strip.rect.left - clip.left}px`,
      top: `${strip.rect.top - clip.top}px`,
      width: `${strip.rect.width}px`,
      height: `${strip.rect.height}px`
    });
    const copy = strip.el.cloneNode(true) as HTMLElement;
    copy.classList.remove(...ANIMATED_CLASSES);
    wrap.appendChild(copy);
    layer.appendChild(wrap);
    strip.el.style.visibility = 'hidden';
    setTimeout(() => {
      if (strip.el.isConnected) strip.el.style.visibility = '';
    }, STRIP_RESTORE_MS);
  });

  const ghost = el.cloneNode(true) as HTMLElement;
  // Not findable as the session any more, and free of any animation the session itself was running.
  ghost.removeAttribute('id');
  ghost.classList.remove(...ANIMATED_CLASSES);
  ghost.classList.add('poof-ghost');
  Object.assign(ghost.style, {
    left: `${rect.left - clip.left}px`,
    top: `${rect.top - clip.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    right: 'auto',
    bottom: 'auto'
  });
  layer.appendChild(ghost);

  const middleX = rect.left - clip.left + rect.width / 2;
  const middleY = rect.top - clip.top + rect.height / 2;
  cloudPuffs(rect).forEach(({ x, y, size, drift }) => {
    const puff = document.createElement('span');
    puff.className = 'poof-cloud';
    Object.assign(puff.style, {
      left: `${middleX + x - size / 2}px`,
      top: `${middleY + y - size / 2}px`,
      width: `${size}px`,
      height: `${size}px`
    });
    puff.style.setProperty('--poof-drift', `${drift}px`);
    layer.appendChild(puff);
  });

  container.appendChild(layer);
  // The session itself is about to leave the store; until it does, only its ghost is seen.
  el.style.visibility = 'hidden';
  setTimeout(() => layer.remove(), POOF_MS);
};

/** Poofs `el` away. Returns whether it did: false for an element that isn't visible, or under reduced motion. */
export const poofElement = (el: HTMLElement): boolean => {
  const measured = measure(el);
  if (measured) poof(measured);
  return !!measured;
};

/**
 * Poofs the planned sessions with these ids that are on screen, all at the same moment. Matched by class as well as
 * id: the calendar gives other kinds of events their own ids too, and those can be the same numbers.
 */
export const poofPlannedSessions = (ids: Array<number | string>): number => {
  const wanted = new Set(ids.map(String));
  if (wanted.size === 0) return 0;
  const measured: Measured[] = [];
  document.querySelectorAll<HTMLElement>('.fc-event.planned-task-event').forEach(el => {
    const m = wanted.has(el.id) ? measure(el) : undefined;
    if (m) measured.push(m);
  });
  measured.forEach(poof);
  return measured.length;
};

// The time sessions just unplanned held, for a moment: the free time the calendar draws there next fades in.
let freedTime: { spans: Array<{ start: number; end: number }>; until: number } | undefined;

/**
 * Notes the time these sessions held (epoch ms), just before they leave the store. The calendar works free and extra
 * time out afresh once they have gone, and draws what it finds in their place at once; with this, that fades in as
 * the cloud clears rather than popping in after it.
 */
export const markFreedTime = (spans: Array<{ start: number; end: number }>) => {
  freedTime = spans.length ? { spans, until: Date.now() + 1000 } : undefined;
};

/** Whether free or extra time from `start` to `end` (epoch ms) is being drawn where a session was just unplanned. */
export const isFreedTime = (start: number, end: number) =>
  !!freedTime && Date.now() < freedTime.until && freedTime.spans.some(span => start < span.end && end > span.start);
